import type { ProfileSummary, PublicProfile } from "@pax/api-contract/common";
import {
  cn,
  focusHaloFlush,
  HapticSurface,
  panelMotion,
  panelRow,
  press,
  Skeleton,
  solidSurface,
  usePanelDismiss,
} from "@pax/ui";
import { type UseInfiniteQueryResult, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Flag, X } from "lucide-react";
import type { AnimationEvent, ReactNode } from "react";
import { AccountAvatar } from "@/features/auth/components/AccountAvatar";
import { formatTimeAgo } from "@/lib/format";
import { ScreenOverlay } from "@/shell/Screen";
import { profileLink } from "../links";
import { friendPagesQuery, mutualFriendPagesQuery, profileStatsQuery } from "../queries";

const joinedMonth = new Intl.DateTimeFormat("en", { month: "long", year: "numeric" });

interface PanelPresence {
  /** True from the moment it is asked to go until it has gone. */
  leaving: boolean;
  onLeft: (event: AnimationEvent<HTMLElement>) => void;
  onClose: () => void;
}

/** A panel over the profile, with the same head and movement as a preset's Details. */
function ProfilePanel({
  title,
  count,
  leaving,
  onLeft,
  onClose,
  children,
}: PanelPresence & { title: string; count?: number; children: ReactNode }) {
  const panel = usePanelDismiss<HTMLDivElement>({
    open: !leaving,
    onDismiss: onClose,
    dismissOnOutsidePress: false,
  });

  return (
    <ScreenOverlay>
      <button
        type="button"
        tabIndex={-1}
        aria-hidden
        onClick={onClose}
        data-leaving={leaving || undefined}
        className="overlay-scrim absolute inset-0 cursor-default"
      />
      <div
        ref={panel}
        role="dialog"
        aria-label={title}
        data-leaving={leaving || undefined}
        onAnimationEnd={onLeft}
        className={`overlay-panel relative flex max-h-full w-full max-w-sm flex-col p-5 ${solidSurface}`}
      >
        <div className="mb-3 flex shrink-0 items-center justify-between gap-4">
          <p className="flex items-baseline gap-2 text-base font-medium">
            {title}
            {count === undefined ? null : (
              <span className="rounded-full bg-foreground/10 px-2 py-0.5 text-xs font-medium text-muted-foreground tabular-nums">
                {count.toLocaleString()}
              </span>
            )}
          </p>
          <button
            type="button"
            onClick={onClose}
            aria-label={`Close ${title.toLowerCase()}`}
            className={`relative flex size-8 shrink-0 items-center justify-center rounded-full bg-foreground/10 text-foreground hover:bg-foreground/15 ${panelMotion} ${press} ${focusHaloFlush}`}
          >
            <X className="size-4" />
            <HapticSurface />
          </button>
        </div>
        <div className="min-h-0 overflow-y-auto">{children}</div>
      </div>
    </ScreenOverlay>
  );
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-6 border-t border-foreground/10 py-2.5 text-sm first:border-t-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{children}</span>
    </div>
  );
}

/**
 * What ⋯ opens: facts about the profile that the page doesn't already show, then, on someone
 * else's profile, the way to report it. On your own, Region opens its editor.
 */
export function ProfileDetailsPanel({
  profile,
  own,
  onEditRegion,
  onReport,
  ...presence
}: PanelPresence & {
  profile: PublicProfile;
  own: boolean;
  onEditRegion: () => void;
  onReport: (() => void) | null;
}) {
  const stats = useQuery(profileStatsQuery(profile.profileId));

  return (
    <ProfilePanel title="Details" {...presence}>
      {own ? (
        <DetailRow label="Region">
          <button
            type="button"
            onClick={onEditRegion}
            className={cn(
              "rounded hover:underline",
              !profile.region && "text-muted-foreground",
              focusHaloFlush,
            )}
          >
            {profile.region ?? "Add"}
          </button>
        </DetailRow>
      ) : profile.region ? (
        <DetailRow label="Region">{profile.region}</DetailRow>
      ) : null}
      <DetailRow label="Joined">{joinedMonth.format(new Date(profile.createdAt))}</DetailRow>
      {profile.lastActiveAt ? (
        <DetailRow label="Last active">{formatTimeAgo(profile.lastActiveAt)}</DetailRow>
      ) : null}
      <DetailRow label="Play time">
        {stats.data ? (
          formatPlayTime(stats.data.playSeconds)
        ) : stats.isError ? (
          "–"
        ) : (
          <Skeleton className="inline-block h-[1lh] w-10 align-middle" />
        )}
      </DetailRow>

      {onReport ? (
        <button
          type="button"
          onClick={onReport}
          className={`mt-4 flex h-10 w-full items-center justify-center gap-2 rounded-full bg-foreground/5 text-sm font-medium text-muted-foreground hover:bg-foreground/10 hover:text-foreground ${panelMotion} ${press} ${focusHaloFlush}`}
        >
          <Flag className="size-3.5" />
          Report profile
        </button>
      ) : null}
    </ProfilePanel>
  );
}

/** Time played in the largest units that still say something: "45m", "12h", "3h 20m". */
function formatPlayTime(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return hours >= 10 || rest === 0 ? `${hours.toLocaleString()}h` : `${hours}h ${rest}m`;
}

function FriendRow({ friend, onOpen }: { friend: ProfileSummary; onOpen: () => void }) {
  return (
    <Link
      {...profileLink(friend)}
      onClick={onOpen}
      className={`relative flex w-full items-center gap-3 rounded-xl px-2 py-1.5 text-left hover:bg-foreground/5 ${panelMotion} ${focusHaloFlush}`}
    >
      <AccountAvatar name={friend.displayName} image={friend.avatar} size={32} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{friend.displayName}</span>
        {friend.username ? (
          <span className="block truncate text-xs text-muted-foreground">@{friend.username}</span>
        ) : null}
      </span>
      <HapticSurface />
    </Link>
  );
}

function FriendRows({
  friends,
  more,
  onOpen,
}: {
  friends: ProfileSummary[] | undefined;
  /** The list's query, which loads its next page when there is one. */
  more: UseInfiniteQueryResult;
  onOpen: () => void;
}) {
  if (!friends) {
    return (
      <div className="flex flex-col gap-2" aria-busy>
        {["a", "b", "c"].map(placeholder => (
          <Skeleton key={placeholder} className="h-11 w-full rounded-xl" />
        ))}
      </div>
    );
  }
  return (
    <div className="flex flex-col">
      {friends.map(friend => (
        <FriendRow key={friend.profileId} friend={friend} onOpen={onOpen} />
      ))}
      {more.hasNextPage ? (
        <button
          type="button"
          disabled={more.isFetchingNextPage}
          onClick={() => void more.fetchNextPage()}
          className={cn(panelRow, "justify-center text-muted-foreground disabled:opacity-50")}
        >
          {more.isFetchingNextPage ? "Loading…" : "Show more"}
          <HapticSurface />
        </button>
      ) : null}
    </div>
  );
}

/**
 * Who the Friends count stands for. On your own profile, your friends. On someone else's, the
 * friends you share with them: nobody else's full list can be read.
 */
export function ProfileFriendsPanel({
  profile,
  own,
  ...presence
}: PanelPresence & { profile: PublicProfile; own: boolean }) {
  const yours = useInfiniteQuery({ ...friendPagesQuery, enabled: own });
  const mutual = useInfiniteQuery({ ...mutualFriendPagesQuery(profile.profileId), enabled: !own });

  return own ? (
    <ProfilePanel title="Friends" count={profile.friendCount} {...presence}>
      <FriendRows
        friends={yours.data?.pages.flatMap(page => page.items.map(entry => entry.profile))}
        more={yours}
        onOpen={presence.onClose}
      />
    </ProfilePanel>
  ) : (
    <ProfilePanel title="Mutual friends" count={mutual.data?.pages[0]?.total} {...presence}>
      <FriendRows
        friends={mutual.data?.pages.flatMap(page => page.profiles)}
        more={mutual}
        onOpen={presence.onClose}
      />
    </ProfilePanel>
  );
}
